import * as React from 'react';

import AdminSisSyncStatusLabel from 'admin/rostering/components/AdminSisSyncStatusLabel';
import Button from 'components/Button';
import TitleHeader from 'components/TitleHeader';
import AdminNoticeBanner from 'admin/components/AdminNoticeBanner';
import Link from 'components/Link';
import Text from 'components/Text';

import { AdminDistrictSisDashboard_organizations_childOrganizations as ChildOrganization } from 'admin/rostering/queries/__generated__/AdminDistrictSisDashboard';

type Props = {
  title: string;
  subtitle: React.ReactNode;
  orgUuid: string;
  childOrganizations: ChildOrganization[];
  canOnlySeeReviewScreen: boolean;
  canOnlySeeRosteringStagingArea: boolean;
};

function renderMatchingBanner(orgUuid: string) {
  const button = (
    <Button
      eventElementName="AdminDistrictSisDashboardCleverHeader-matchYourSchools"
      href={`/sync_setup/matching?district_uuid=${orgUuid}`}>
      Match your schools
    </Button>
  );
  return (
    <AdminNoticeBanner glyph="success-checkmark-stroke" color="brand" actionElement={button}>
      <Text element="div">Finish setup by mapping your schools from Clever correctly.</Text>
    </AdminNoticeBanner>
  );
}

function renderSetupBanner(orgUuid: string) {
  return (
    <AdminNoticeBanner glyph="success-checkmark-stroke" color="brand" actionElement={null}>
      <Text element="div">
        Get started by logging into Clever. View details in the upper right or{' '}
        <Link href={`/sync_setup?district_uuid=${orgUuid}`} weight="semibold">
          click here
        </Link>
        .
      </Text>
    </AdminNoticeBanner>
  );
}

function renderAwaitingReviewBanner() {
  return (
    <AdminNoticeBanner glyph="clock" color="sunrise" actionElement={null}>
      Our implementation team is reviewing your data. You'll receive an email with next steps in two
      business days.
    </AdminNoticeBanner>
  );
}

function renderStagingEnvironmentBanner() {
  return (
    <AdminNoticeBanner glyph="clock" color="koala" actionElement={null}>
      Review each school's sync to fix errors. When you're ready to push data live, contact our
      implementation team.
    </AdminNoticeBanner>
  );
}

export default function AdminDistrictSisDashboardCleverHeader(props: Props) {
  const {
    title,
    subtitle,
    childOrganizations,
    orgUuid,
    canOnlySeeReviewScreen,
    canOnlySeeRosteringStagingArea,
  } = props;

  const noCleverSchoolsSetUp = childOrganizations.every(
    org => org?.sisSyncOverview?.syncStatus === 'not_set_up'
  );
  const someCleverSchoolsNotSetUp = childOrganizations.some(
    org => org?.sisSyncOverview?.syncStatus === 'not_set_up'
  );

  const showSetupBanner = noCleverSchoolsSetUp;
  const showMatchingBanner = !showSetupBanner && someCleverSchoolsNotSetUp;
  const isInactive = showSetupBanner || showMatchingBanner;

  function renderTitle() {
    if (canOnlySeeReviewScreen || canOnlySeeRosteringStagingArea) {
      if (isInactive) {
        return <AdminSisSyncStatusLabel status="inactive" />;
      } else {
        return <AdminSisSyncStatusLabel status="pending" />;
      }
    }

    return title;
  }

  return (
    <TitleHeader
      title={renderTitle()}
      subtext={subtitle}
      action={
        <Button level="secondary" href={`/sync_setup?district_uuid=${orgUuid}`}>
          SIS sync setup
        </Button>
      }
      actionPlacement="right"
      className="AdminDistrictSisDashboardCleverHeader">
      {showSetupBanner && renderSetupBanner(orgUuid)}
      {showMatchingBanner && renderMatchingBanner(orgUuid)}
      {canOnlySeeReviewScreen && renderAwaitingReviewBanner()}
      {canOnlySeeRosteringStagingArea && renderStagingEnvironmentBanner()}
    </TitleHeader>
  );
}
